import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hexToHsl(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return [h, Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = ln - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function isValidHex(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

// ---------------------------------------------------------------------------
// Sub-component: a single labelled HSL slider
// ---------------------------------------------------------------------------

function HslSlider({
  label,
  value,
  min = 0,
  max,
  step = 1,
  onChange,
  trackStyle,
  tabIndex,
}: {
  label: string;
  value: number;
  min?: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  trackStyle: React.CSSProperties;
  tabIndex?: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-500">{label}</label>
        <span className="w-10 text-right text-xs text-slate-600 tabular-nums">
          {value}
        </span>
      </div>
      <div className="relative h-3 w-full rounded-full" style={trackStyle}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          tabIndex={tabIndex}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          style={{ margin: 0 }}
        />
        {/* Thumb indicator */}
        <div
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md"
          style={{ left: `${((value - min) / (max - min)) * 100}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CustomColorPicker({
  color,
  onChange,
  className,
  tabIndex,
}: {
  color: string;
  onChange: (color: string) => void;
  className?: string;
  tabIndex?: number;
}) {
  const initialHsl = useMemo<[number, number, number]>(() => {
    if (isValidHex(color)) return hexToHsl(color);
    return [0, 0, 50];
  }, []);

  const [h, setH] = useState(initialHsl[0]);
  const [s, setS] = useState(initialHsl[1]);
  const [l, setL] = useState(initialHsl[2]);

  // Sync inward when `color` prop changes from outside (e.g. swatch click)
  useEffect(() => {
    if (isValidHex(color)) {
      const [nh, ns, nl] = hexToHsl(color);
      setH(nh);
      setS(ns);
      setL(nl);
    }
  }, [color]);

  const emit = useCallback(
    (nh: number, ns: number, nl: number) => {
      onChange(hslToHex(nh, ns, nl));
    },
    [onChange],
  );

  const previewColor = hslToHex(h, s, l);

  // Gradient tracks
  const hueTrack: React.CSSProperties = {
    background:
      'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
  };

  const satTrack: React.CSSProperties = {
    background: `linear-gradient(to right, hsl(${h}, 0%, ${l}%), hsl(${h}, 100%, ${l}%))`,
  };

  const lightTrack: React.CSSProperties = {
    background: `linear-gradient(to right, hsl(${h}, ${s}%, 0%), hsl(${h}, ${s}%, 50%), hsl(${h}, ${s}%, 100%))`,
  };

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      {/* Preview swatch */}
      <div
        className="h-8 w-full rounded-md border border-slate-200"
        style={{ backgroundColor: previewColor }}
      />

      <HslSlider
        label="Hue"
        value={h}
        max={360}
        trackStyle={hueTrack}
        tabIndex={tabIndex}
        onChange={(v) => {
          setH(v);
          emit(v, s, l);
        }}
      />
      <HslSlider
        label="Saturation"
        value={s}
        max={100}
        trackStyle={satTrack}
        tabIndex={tabIndex}
        onChange={(v) => {
          setS(v);
          emit(h, v, l);
        }}
      />
      <HslSlider
        label="Lightness"
        value={l}
        max={100}
        trackStyle={lightTrack}
        tabIndex={tabIndex}
        onChange={(v) => {
          setL(v);
          emit(h, s, v);
        }}
      />

      <p className="text-center font-mono text-xs text-slate-400">
        {previewColor}
      </p>
    </div>
  );
}
